import React, { useCallback, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch } from 'react-redux';

import {
    documentApi,
    useCreateConventionMutation,
    useInitiateConventionSignatureMutation,
} from 'api/main';
import { signatureDialogSet } from 'reducers/user';
import { notificationCreated } from 'reducers/appNotification';
import { downloadClick } from 'utils/helpers';
import { useMatchAtLeastOneRole, useUserRoleMatchCategory } from 'utils/hooks';
import { conventionStatus, roleCategories, RoleNames } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import InfoBox from './InfoBox';
import { ActionButton, CustomButton, Loader } from 'components';
import { Box, Typography, Checkbox } from '@mui/material';

const buttonStyles = { padding: '5px 8px', minWidth: '250px' };
const statusStyles = { marginLeft: '25px' };

export const ConventionButton = ({ convention, fundId, sx }) => {
    const dispatch = useDispatch();

    const [initiateConventionSignature, { isLoading }] = useInitiateConventionSignatureMutation();
    const canSignConvention = useMatchAtLeastOneRole([RoleNames.PROVIDER_SIGNATORY]);
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);

    const handleInitiateSignature = useCallback(() => {
        initiateConventionSignature(convention.id);
    }, [initiateConventionSignature, convention]);

    const handleOpenConfirmSignature = useCallback(
        () => dispatch(signatureDialogSet({ open: true, convention })),
        [convention, dispatch]
    );

    switch (convention.status) {
        case conventionStatus.NOT_SIGNED:
            return (
                <CustomButton
                    sx={theme => ({
                        ...buttonStyles,
                        ...(typeof sx === 'function' ? sx(theme) : sx),
                    })}
                    id={fundId}
                    size="small"
                    label={texts.ENVOYER_POUR_SIGNATURE}
                    variant="contained"
                    onClick={handleInitiateSignature}
                    loading={isLoading}
                />
            );
        case conventionStatus.REQUESTED_DISTRIBUTOR:
            return <Typography sx={statusStyles}>{texts.REQUESTED_BY_DISTRIBUTOR}</Typography>;
        case conventionStatus.SIGNATURE_REQUESTED:
            return <Typography sx={statusStyles}>{texts.AWAITING_PARTNER_SIGNATURE}</Typography>;
        case conventionStatus.PARTIALLY_SIGNED:
            return !userIsProvider ? (
                <Typography sx={statusStyles}>{texts.AWAITING_PROVIDER_SIGNATURE}</Typography>
            ) : (
                <ActionButton
                    sx={theme => ({
                        ...buttonStyles,
                        ...(typeof sx === 'function' ? sx(theme) : sx),
                    })}
                    size="small"
                    variant="contained"
                    onClick={handleOpenConfirmSignature}
                    disabled={!canSignConvention}
                >
                    {texts.CHOOSE_SIGNER}
                </ActionButton>
            );
        case conventionStatus.FULLY_SIGNED:
            return <Typography sx={statusStyles}>{texts.SIGNED_BY_STAKEHOLDERS}</Typography>;
        default:
            return null;
    }
};

ConventionButton.propTypes = {
    convention: PropTypes.object,
    fundId: PropTypes.number,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

const ConventionDoc = ({ convention, fundId }) => {
    const [downloading, setDownloading] = useState(false);
    const dispatch = useDispatch();

    const setNotification = useCallback(
        messageKey => {
            dispatch(notificationCreated(messageKey));
        },
        [dispatch]
    );

    const handleDownload = useCallback(
        doc => {
            setDownloading(true);
            documentApi
                .downloadDocumentById(doc.id)
                .then(response => {
                    const bytes = new Uint8Array(response.data);
                    const blob = new Blob([bytes], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    downloadClick(url, doc.name);
                    setDownloading(false);
                })
                .catch(() => {
                    setNotification('FAILED_DOCUMENT_DOWNLOAD');
                    setDownloading(false);
                });
        },
        [setNotification]
    );

    return (
        <Box display="flex" alignItems="center">
            {convention.document && (
                <Typography
                    sx={{ marginLeft: '10px', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => handleDownload(convention.document)}
                    component="span"
                    display="block"
                >
                    <Box width="100px" display="flex" justifyContent="center">
                        <Loader size={15} marginTop={0} loading={downloading}>
                            {texts.CONVENTION}
                        </Loader>
                    </Box>
                </Typography>
            )}
            <ConventionButton convention={convention} fundId={fundId} />
        </Box>
    );
};

ConventionDoc.propTypes = {
    convention: PropTypes.object,
    fundId: PropTypes.number,
};

const Convention = ({ fund, company }) => {
    const [selected, setSelected] = useState(false);
    const [createConvention, { isLoading }] = useCreateConventionMutation();

    const currentConvention = company.conventions.find(c => +c.investmentFund?.id === fund.id);

    const isConventionGenerated =
        currentConvention && currentConvention.status !== conventionStatus.REQUESTED_DISTRIBUTOR;

    const isConventionRequested =
        currentConvention && currentConvention.status === conventionStatus.REQUESTED_DISTRIBUTOR;

    const handleChange = useCallback(({ target: { checked } }) => setSelected(checked), []);

    const handleGenerateConvention = useCallback(() => {
        createConvention({ userId: company.user.id, fundId: fund.id, companyId: company.id });
    }, [company, createConvention, fund]);

    if (!fund.visible) return null;

    return (
        <Box display="flex" minHeight="60px" alignItems="center" justifyContent="flex-start">
            <Checkbox
                color="primary"
                disableRipple
                checked={selected || isConventionGenerated}
                disabled={!isConventionRequested}
                onChange={handleChange}
            />
            <Typography component="span">{`${fund.type} - ${fund.name}`}</Typography>
            {currentConvention && <ConventionDoc fundId={fund.id} convention={currentConvention} />}
            {!isConventionGenerated && selected && (
                <CustomButton
                    sx={buttonStyles}
                    id={fund.id}
                    size="small"
                    label={texts.GENERATE_CONVENTION}
                    variant="contained"
                    onClick={handleGenerateConvention}
                    loading={isLoading}
                />
            )}
        </Box>
    );
};

Convention.propTypes = {
    fund: PropTypes.object,
    company: PropTypes.object,
};

const ConventionsSection = ({ isCommercialisateur, company, fundList = [], loader }) => {
    const [createConvention, { isLoading }] = useCreateConventionMutation();

    const handleGenerateConvention = useCallback(
        ({ currentTarget: { id } }) => {
            createConvention({ userId: company.user.id, fundId: id, companyId: company.id });
        },
        [company, createConvention]
    );

    return (
        <InfoBox title={texts.CONVENTION_TITLE}>
            <Loader loading={loader} marginTop={0}>
                {isCommercialisateur ? (
                    <Box px={0}>
                        {fundList
                            .filter(f => f.visible)
                            .map(f => (
                                <Convention key={f.id} fund={f} company={company} />
                            ))}
                    </Box>
                ) : (
                    <React.Fragment>
                        {company.conventions?.length === 0 ? (
                            <CustomButton
                                sx={buttonStyles}
                                size="small"
                                label={texts.GENERATE_CONVENTION}
                                variant="contained"
                                onClick={handleGenerateConvention}
                                loading={isLoading}
                            />
                        ) : (
                            <Box display="flex" flexDirection="column" alignItems="baseline">
                                {company.conventions?.map(convention => (
                                    <Box key={convention.id} display="flex" justifyContent="start">
                                        <Typography component="span">
                                            {convention.investmentFund.name}
                                        </Typography>
                                        <ConventionDoc convention={convention} />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </React.Fragment>
                )}
            </Loader>
        </InfoBox>
    );
};

ConventionsSection.propTypes = {
    company: PropTypes.object,
    fundsLoader: PropTypes.bool,
    isCommercialisateur: PropTypes.bool,
    fundList: PropTypes.array,
};

export default ConventionsSection;
